import React, { memo, useCallback, useMemo, useState } from 'react'
import Modal from '@mui/material/Modal'
import tw from 'twin.macro'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { Button } from '../shared/Button'
import {
  CircleCancelMajor,
  CircleCancelMinor,
  CircleTickMajor,
  PauseCircleMajor,
  SendMajor,
} from '@shopify/polaris-icons'
import { colors } from 'src/theme'
import { handleApiError } from 'src/utils/errorHandlers'
import { Typography } from '@mui/material'
import { DataRow } from '../shared/DataRow'
import { MCStatusChip } from '../mc-list/shared/MCStatusChip'
import { MCStatus } from 'src/constants/mc/enums'
import { useDispatch } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  ActivateMC,
  DeactivateMC,
  RejectMCInvite,
  ResendMCInvite,
  UpdateMCExternalId,
} from 'src/constants/actionTypes'
import { MCDetails } from 'src/interfaces/mc'
import { showToast } from 'src/utils/toast'
import { MCDetailsTabs } from './tabs/MCDetailsTabs'
import { MCTab } from './tabs/enums'
import { OnboardingStepsTab } from './tabs/OnboardingStepsTab'
import { AddOnsTab } from './tabs/AddOnsTab'
import { MCStepHeader } from './shared/MCStepHeader'
import { MCStepDocuments } from './shared/MCStepDocuments'
import { MCStepDetails } from './types'
import dayjs from 'dayjs'
import { SensitiveActionModal } from '../shared/SensitiveActionModal'
import { SubmitConfirmModal } from '../shared/SubmitConfirmModal'

interface Props {
  mcDetails: MCDetails
  onClose(): void
  onRefresh(): Promise<void> | void
}

const MCDetailsModalComponent = ({ mcDetails, onClose, onRefresh }: Props) => {
  const dispatch = useDispatch()

  const [stepDetails, setStepDetails] = useState<MCStepDetails | null>(null)

  const container = useMemo(() => document.getElementById('root'), [])

  const [isActivateConfirmOpen, setIsActivateConfirmOpen] = useState(false)
  const [isDeactivateConfirmOpen, setIsDeactivateConfirmOpen] = useState(false)

  const [isResendLoading, setIsResendLoading] = useState(false)
  const [isRejectLoading, setIsRejectLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<MCTab>(MCTab.Onboarding)

  const handleOpenActivateConfirm = useCallback(() => {
    setIsActivateConfirmOpen(true)
  }, [])

  const handleCloseActivateConfirm = useCallback(() => {
    setIsActivateConfirmOpen(false)
  }, [])

  const handleOpenDeactivateConfirm = useCallback(() => {
    setIsDeactivateConfirmOpen(true)
  }, [])

  const handleCloseDeactivateConfirm = useCallback(() => {
    setIsDeactivateConfirmOpen(false)
  }, [])

  const handleActivate = useCallback(async () => {
    setIsResendLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        ActivateMC.request({
          masterContractorId: mcDetails.partner.id,
        }),
      )

      await onRefresh()
      handleCloseActivateConfirm()
    } catch (error) {
      handleApiError(error)
    }

    setIsResendLoading(false)
  }, [mcDetails.id, onRefresh])

  const handleDeactivate = useCallback(async () => {
    setIsResendLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        DeactivateMC.request({
          masterContractorId: mcDetails.partner.id,
        }),
      )

      await onRefresh()
      handleCloseDeactivateConfirm()
    } catch (error) {
      handleApiError(error)
    }

    setIsResendLoading(false)
  }, [mcDetails.id, onRefresh])

  const handleResendInvite = useCallback(async () => {
    setIsResendLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        ResendMCInvite.request({
          masterContractorId: mcDetails.partner.id,
        }),
      )

      showToast("The reminder has been resent to the master contractor's email address!")

      onRefresh()
    } catch (error) {
      handleApiError(error)
    }

    setIsResendLoading(false)
  }, [mcDetails.id, onRefresh])

  const handleReject = useCallback(async () => {
    setIsRejectLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        RejectMCInvite.request({
          masterContractorRelationId: mcDetails.masterContractorRelationId,
        }),
      )

      onClose()
      onRefresh()
    } catch (error) {
      handleApiError(error)
    }

    setIsRejectLoading(false)
  }, [mcDetails.id, onRefresh, onClose])

  const handleEditExternalId = useCallback(
    async (externalId: string) => {
      if (externalId === mcDetails.externalId) {
        return
      }

      try {
        await createAsyncAction(
          dispatch,
          UpdateMCExternalId.request({
            masterContractorId: mcDetails.masterContractorRelationId,
            externalId,
          }),
        )

        await onRefresh()

        showToast('Master contractor ID saved.')
      } catch (err: any) {
        handleApiError(err)
      }
    },
    [mcDetails.externalId, mcDetails.id, onRefresh],
  )

  const closeStepDetails = useCallback(() => {
    setStepDetails(null)
  }, [])

  return (
    <>
      <Modal
        container={container}
        open
        onClose={onClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
        css={tw`flex flex-row w-full h-full`}
      >
        <>
          <div css={tw`w-[858px] h-full bg-white flex flex-col`}>
            <ModalHeader
              title="Master contractor details"
              onClose={onClose}
              className={tw`text-black h-[48px]`}
            />
            <div css={tw`flex h-[calc(100vh-48px)]`}>
              <div
                css={tw`max-w-[400px] flex-1 flex flex-col justify-between border-0 border-r border-solid border-r-[#EDEDED] px-4`}
              >
                <div>
                  <Typography css={tw`pt-4 py-1`} variant="h2">
                    {mcDetails.partner.displayName}
                  </Typography>
                  <Typography
                    css={tw`pb-4 border-0 border-b border-solid border-b-[#EDEDED]`}
                    variant="body2"
                    color={colors.SHADES_GREY_400}
                  >
                    {mcDetails.id}
                  </Typography>
                  <DataRow
                    title="Status"
                    component={
                      <MCStatusChip
                        status={
                          mcDetails.status === MCStatus.Invited ? 'pending' : mcDetails.status
                        }
                      />
                    }
                  />
                  <DataRow
                    title="External ID"
                    text={mcDetails.externalId || '-'}
                    value={mcDetails.externalId || '-'}
                    onEdit={handleEditExternalId}
                    showCopyButton
                  />
                  <DataRow
                    title="Contact name"
                    text={`${mcDetails.partner.owner.firstName} ${mcDetails.partner.owner.lastName}`}
                  />
                  <DataRow title="Email" text={mcDetails.partner.owner.email} />
                  <DataRow
                    title="DOT number"
                    text={mcDetails.partner.partnerInformation?.dotNumber || '-'}
                  />
                  <DataRow title="Fleet size" text={mcDetails.activeDriversCount || 0} />
                </div>
                <div css={tw`py-4 border-0 border-t border-solid border-[#EDEDED]`}>
                  {mcDetails.status === MCStatus.Invited && (
                    <>
                      <Button
                        loading={isResendLoading}
                        color="light"
                        size="small"
                        className={tw`w-full mb-2`}
                        rightIcon={<SendMajor width={20} fill={colors.PRIMARY_PURPLE_500} />}
                        onClick={handleResendInvite}
                      >
                        Resend an invite
                      </Button>
                      <Button
                        loading={isRejectLoading}
                        color="grey"
                        size="small"
                        className={tw`w-full`}
                        rightIcon={<CircleCancelMajor width={20} />}
                        onClick={handleReject}
                      >
                        Reject master contractor
                      </Button>
                    </>
                  )}
                  {mcDetails.status === MCStatus.Rejected && (
                    <Button
                      loading={isResendLoading}
                      color="grey"
                      size="small"
                      className={tw`w-full mb-2`}
                      rightIcon={<CircleTickMajor width={20} />}
                      onClick={handleResendInvite}
                    >
                      Unreject master contractor
                    </Button>
                  )}
                  {mcDetails.status === MCStatus.Deactivated && (
                    <Button
                      loading={isResendLoading}
                      color="grey"
                      size="small"
                      className={tw`w-full`}
                      rightIcon={<CircleTickMajor width={20} />}
                      onClick={handleOpenActivateConfirm}
                    >
                      Activate master contractor
                    </Button>
                  )}
                  {mcDetails.status === MCStatus.Activated && (
                    <Button
                      loading={isResendLoading}
                      color="grey"
                      size="small"
                      className={tw`w-full`}
                      rightIcon={<PauseCircleMajor width={20} />}
                      onClick={handleOpenDeactivateConfirm}
                    >
                      Deactivate master contractor
                    </Button>
                  )}
                </div>
              </div>
              <div css={tw`w-[458px] flex-1 bg-[#EDEDED]`}>
                <MCDetailsTabs selectedTab={activeTab} onClick={setActiveTab} />
                {activeTab === MCTab.Onboarding && (
                  <OnboardingStepsTab mcDetails={mcDetails} onSelectStep={setStepDetails} />
                )}
                {activeTab === MCTab.Addons && (
                  <AddOnsTab mcDetails={mcDetails} onUpdate={onRefresh} />
                )}
              </div>
            </div>
          </div>
          {stepDetails && (
            <div css={tw`flex flex-1 p-6 max-w-[560px] w-full pointer-events-none`}>
              <div
                css={tw`rounded-2xl shadow-lg bg-white py-4 min-w-[380px] flex flex-col pointer-events-auto overflow-y-auto h-full w-full`}
              >
                <div css={tw`px-4`}>
                  <MCStepHeader
                    stepStatus={stepDetails.stepStatus}
                    stepType={stepDetails.stepType}
                    expiresSoon={
                      stepDetails.expiresAt
                        ? dayjs(stepDetails.expiresAt).diff(dayjs(), 'day') <= 30 &&
                          dayjs(stepDetails.expiresAt).isAfter(dayjs())
                        : false
                    }
                  />
                  <div css={tw`w-full mt-4 h-[1px] bg-[#EDEDED]`} />
                </div>
                <div css={tw`flex-1 overflow-y-auto px-4`}>
                  {stepDetails.stepData.map((item) => (
                    <DataRow key={item.title} title={item.title} text={item.text} />
                  ))}
                  {!!stepDetails.documents?.length && (
                    <MCStepDocuments
                      documents={stepDetails.documents}
                      header={
                        <MCStepHeader
                          stepStatus={stepDetails.stepStatus}
                          stepType={stepDetails.stepType}
                          expiresSoon={
                            stepDetails.expiresAt
                              ? dayjs(stepDetails.expiresAt).diff(dayjs(), 'day') <= 30 &&
                                dayjs(stepDetails.expiresAt).isAfter(dayjs())
                              : false
                          }
                        />
                      }
                    />
                  )}
                </div>

                <div css={tw`flex justify-end mt-4 flex-col gap-4 px-4`}>
                  <Button
                    fullWidth
                    color="white"
                    size="small"
                    onClick={closeStepDetails}
                    endIcon={<CircleCancelMinor width={20} />}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      </Modal>
      <SubmitConfirmModal
        isOpen={isActivateConfirmOpen}
        title="Activate master contractor?"
        text="Are you sure you want to activate master contractor?"
        submitLabel="Activate"
        onClose={handleCloseActivateConfirm}
        onSubmit={handleActivate}
      />
      <SensitiveActionModal
        isOpen={isDeactivateConfirmOpen}
        title="Deactivate master contractor?"
        text="Are you sure you want to deactivate master contractor?"
        submitLabel="Deactivate"
        onClose={handleCloseDeactivateConfirm}
        onSubmit={handleDeactivate}
      />
    </>
  )
}

export const MCDetailsModal = memo(MCDetailsModalComponent)
